import { useState } from 'react'

import Box from '@mui/material/Box'
import ArrowBackIosIcon from '@mui/icons-material/ArrowBackIos'
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import { Navigation, Pagination, Autoplay, Lazy } from 'swiper'
import { SwiperSlide } from 'swiper/react'
import 'swiper/css'
import styled from '@emotion/styled'

import DesktopPaginationWithTitle from 'components/common/Banner/DesktopPaginationWithTitle'
import { StyledSwiper } from 'components/common/Banner/StyledSwiper'
import { BannerImg } from 'components/common/Banner'
import { sendBannerClick } from 'utils/hooks/analytics'
import { genPathByNavData } from 'utils/api'
import { useBannerGAForCarousel } from 'utils/hooks/useBannerGA'

const modules = [Navigation, Pagination, Autoplay, Lazy]

const Img = styled(BannerImg)`
  ${({ theme, $clickable }) => `
    min-height: unset;
    object-fit: cover;
    cursor: ${$clickable ? 'pointer' : 'default'};
    aspect-ratio: 3 / 2;

    ${theme.breakpoints.up('lg')} {
      aspect-ratio: 4 / 1;
    }
  `}
`

const swiperSettings = {
  spaceBetween: 24,
  slidesPerView: 'auto',
  centeredSlides: true,
  loop: true,
  pagination: { el: '.banner-pagination', clickable: true },
  slideToClickedSlide: true,
}

const swiperAutoplay = {
  delay: 5000,
  disableOnInteraction: false,
}

const BannerSlide = ({ banner, onBannerClick, isFirst, isMobile }) => {
  const src = isMobile
    ? banner?.mobileImage?.url || banner?.image?.url
    : banner?.image?.url || banner?.mobileImage?.url
  const isClickable = banner?.navData?.page !== 'default'

  if (!src) return null

  return isFirst || !isMobile ? (
    <Img
      src={src}
      $clickable={isClickable}
      onClick={isClickable ? onBannerClick : undefined}
    />
  ) : (
    <Img
      data-src={src}
      $clickable={isClickable}
      onClick={isClickable ? onBannerClick : undefined}
      className="swiper-lazy"
    />
  )
}

const VenderBannerCarousel = ({ banners, isMobile, venderName, venderId }) => {
  const [swiper, setSwiper] = useState(null)
  const [swiperIndex, setSwiperIndex] = useState(0)
  const [hovered, setHovered] = useState(false)
  const theme = useTheme()
  const isDesktop = useMediaQuery(theme.breakpoints.up('lg'))
  const safeBanners = Array.isArray(banners)
    ? banners.filter((banner) => banner?.image?.url || banner?.mobileImage?.url)
    : []

  const onNextClick = () => swiper?.slideNext()
  const onPrevClick = () => swiper?.slidePrev()
  const onIndexChange = (s) => {
    if (s.realIndex > -1) setSwiperIndex(s.realIndex)
  }
  const onPaginationIndexClick = (index) =>
    swiper?.slideTo(safeBanners.length + index)

  const onBannerClick = (banner) => async () => {
    sendBannerClick({
      banner,
      bannerLocation: '廠商首頁',
      venderName,
      venderId,
    })
    const { path, isExternalLink } = await genPathByNavData(banner?.navData)
    if (path) {
      const url =
        !isExternalLink && /^\/service\//.test(path)
          ? `${path}${
              path.includes('?') ? '&' : '?'
            }preselect_vender=${venderId}`
          : path
      window.open(url, '_blank')
    }
  }

  useBannerGAForCarousel({
    bannerData: safeBanners,
    bannerLocation: '廠商首頁',
    venderName,
    venderId,
    swiper,
    isRepeated: false,
  })

  useBannerGAForCarousel({
    bannerData: safeBanners,
    bannerLocation: '廠商首頁',
    venderName,
    venderId,
    swiper,
    isRepeated: true,
  })

  if (!safeBanners.length) return null

  const swiperNavigation = {
    nextEl: `.vender-banner-instance-${safeBanners[0]?._id} .banner-swiper-next`,
    prevEl: `.vender-banner-instance-${safeBanners[0]?._id} .banner-swiper-prev`,
  }

  const swiperLazyOptions = isMobile
    ? { loadOnTransitionStart: true, loadPrevNext: true }
    : false

  return (
    <Box mb={18}>
      <Box
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <StyledSwiper
          modules={modules}
          {...swiperSettings}
          preloadImages={!isMobile}
          lazy={swiperLazyOptions}
          watchSlidesProgress={isMobile}
          loopedSlides={safeBanners.length}
          navigation={safeBanners.length > 1 ? swiperNavigation : false}
          autoplay={safeBanners.length > 1 ? swiperAutoplay : false}
          onAfterInit={(s) => setSwiper(s)}
          onRealIndexChange={onIndexChange}
        >
          {safeBanners.length > 1 && (
            <>
              {hovered && (
                <Box
                  className={`vender-banner-instance-${safeBanners[0]?._id}`}
                >
                  <div className="banner-swiper-next" onClick={onNextClick}>
                    <ArrowForwardIosIcon fontSize="small" />
                  </div>
                  <div className="banner-swiper-prev" onClick={onPrevClick}>
                    <ArrowBackIosIcon fontSize="small" />
                  </div>
                </Box>
              )}
              <div className="banner-pagination" />
            </>
          )}

          {safeBanners.map((banner, index) => (
            <SwiperSlide key={banner._id || index}>
              <BannerSlide
                banner={banner}
                isMobile={isMobile}
                isFirst={index === 0}
                onBannerClick={onBannerClick(banner)}
              />
            </SwiperSlide>
          ))}

          {isDesktop && (
            <DesktopPaginationWithTitle
              data={safeBanners}
              currentIndex={swiperIndex}
              onPaginationIndexClick={onPaginationIndexClick}
            />
          )}
        </StyledSwiper>
      </Box>
    </Box>
  )
}

export default VenderBannerCarousel
